import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithRef,
} from 'react';

import s from './Input.module.css';
import { clsx } from 'clsx';
import { useInput } from './InputRoot.js';

interface InputControlProps extends ComponentPropsWithRef<'input'> {
  className?: string;
  id: string;
  name: string;
}

function InputControl({
  className,
  placeholder,
  type = 'text',
  onBlur,
  onChange,
  onFocus,
  ref: externalRef,
  ...attrs
}: InputControlProps) {
  const { variant } = useInput();
  const [isPlaceholderVisible, setIsPlaceholderVisible] = useState(false);
  const internalRef = useRef<HTMLInputElement>(null);

  const mergeRefs = useCallback(
    (element: HTMLInputElement | null) => {
      // Update internal ref
      internalRef.current = element;

      // Update external ref if provided
      if (externalRef) {
        if (typeof externalRef === 'function') {
          externalRef(element);
        } else if (externalRef && typeof externalRef === 'object') {
          externalRef.current = element;
        }
      }
    },
    [externalRef],
  );

  const isInputFocused = useCallback(
    () => document.activeElement === internalRef.current,
    [],
  );

  // Note: If Safari supports nested pseudo selectors inside :has in CSS, we can remove
  // handleChange, handleFocus, and handleBlur and use the :has() selector in CSS
  // We need this now to set data-placeholder-shown attribute properly to use in CSS.

  useEffect(() => {
    if (!internalRef.current) return;
    const initialValue = internalRef.current.value;
    setIsPlaceholderVisible(initialValue === '' && !isInputFocused());
  }, [internalRef?.current?.value, isInputFocused]);

  const handleBlur: ComponentPropsWithRef<'input'>['onBlur'] = (e) => {
    const { value } = e.target;
    setIsPlaceholderVisible(value === '');

    if (onBlur) {
      onBlur(e);
    }
  };

  const handleChange: ComponentPropsWithRef<'input'>['onChange'] = (e) => {
    const { value } = e.target;

    setIsPlaceholderVisible(value.length < 1 && !isInputFocused());

    if (onChange) {
      onChange(e);
    }
  };

  const handleFocus: ComponentPropsWithRef<'input'>['onFocus'] = (e) => {
    setIsPlaceholderVisible(false);
    if (onFocus) {
      onFocus(e);
    }
  };

  return (
    <div className={clsx(s.inputWrap, className)}>
      <input
        className={clsx(s.inputControl)}
        data-placeholder-shown={isPlaceholderVisible}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        ref={mergeRefs}
        type={type}
        aria-invalid={variant === 'error' ? true : undefined}
        {...attrs}
      />
    </div>
  );
}

export { InputControl, type InputControlProps };
